import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '../ui/BaseModal';
import { useArchiveDocument } from '../../services/documents.service';

interface ArchiveDocumentModalProps {
  companyId: string;
}

export interface ArchiveDocumentModalRef {
  open: (documentId: string, documentName: string) => void;
}

export const ArchiveDocumentModal = forwardRef<ArchiveDocumentModalRef, ArchiveDocumentModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [documentName, setDocumentName] = useState<string>('');
    const [isConfirmed, setIsConfirmed] = useState(false);

    const archiveDocumentMutation = useArchiveDocument(companyId);

    useImperativeHandle(ref, () => ({
      open: (id: string, name: string) => {
        setDocumentId(id);
        setDocumentName(name);
        setIsConfirmed(false);
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        setIsConfirmed(false);
        setDocumentId(null);
        setDocumentName('');
      }
    }, [isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!documentId || !isConfirmed) return;

      archiveDocumentMutation.mutate(documentId, { onSuccess: handleClose });
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const isSubmitting = archiveDocumentMutation.isPending;
    const canSubmit = isConfirmed && !isSubmitting;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 bg-gray-100 rounded-md text-gray-900 font-medium hover:bg-gray-200 transition"
          disabled={isSubmitting}
        >
          {t('documents.archive_document_modal.cancel')}
        </button>
        <button
          type="submit"
          form="archive-document-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md font-medium transition disabled:opacity-50"
          disabled={!canSubmit}
        >
          {t('documents.archive_document_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.archive_document_modal.title')}
        footer={footer}
        maxWidth="md"
      >
        <form id="archive-document-form" onSubmit={handleSubmit} className="space-y-6">
          <div>
            <p className="text-base font-medium text-gray-900 mb-2">
              {t('documents.archive_document_modal.message')}
            </p>
            <div className="border border-gray-200 rounded-md px-3 py-3">
              <span className="text-sm font-medium text-gray-900">{documentName}</span>
            </div>
          </div>

          <p className="text-sm text-gray-600">{t('documents.archive_document_modal.warning')}</p>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-900">
              {t('documents.archive_document_modal.confirm_checkbox')}
            </span>
          </label>
        </form>
      </BaseModal>
    );
  },
);

ArchiveDocumentModal.displayName = 'ArchiveDocumentModal';
